package com.essntl.features.file.ui.essntlgallery

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.essntl.core.utils.utils.capitalise
import com.essntl.features.file.domain.model.FileModel
import com.essntl.features.file.ui.components.GalleryGrid
import com.essntl.uikit.components.button.outline.EsLargeOutlineButton
import com.essntl.uikit.components.button.primary.EsLargePrimaryButton
import com.essntl.uikit.components.iconbutton.outline.EsSmallOutlineIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import com.mohamedrejeb.calf.core.LocalPlatformContext
import com.mohamedrejeb.calf.io.getName
import com.mohamedrejeb.calf.io.getPath
import com.mohamedrejeb.calf.picker.FilePickerFileType
import com.mohamedrejeb.calf.picker.FilePickerSelectionMode
import com.mohamedrejeb.calf.picker.rememberFilePickerLauncher
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

@Composable
fun EssntlGallery(
    entity: EssntlGalleryState.Entity?,
    isOpen: Boolean,
    onDismissRequest: () -> Unit,
    multipleSelect: Boolean,
    onAddImages: (images: List<FileModel>) -> Unit,
) {
    val viewModel = rememberEssntlGalleryViewModel(entity = entity)
    val state by viewModel.state.collectAsState()

    if (isOpen) {
        Dialog(
            onDismissRequest = onDismissRequest,
            properties = DialogProperties(
                usePlatformDefaultWidth = false,
            ),
        ) {

            EssntlGalleryUI(
                onDismissRequest = onDismissRequest,
                state = state,
                onEvent = { event -> viewModel.onEvent(event) },
                multipleSelect = multipleSelect,
                onAddImages = onAddImages,
            )
        }
    }
}

@OptIn(ExperimentalUuidApi::class)
@Composable
fun EssntlGalleryUI(
    onDismissRequest: () -> Unit,
    state: EssntlGalleryState,
    onEvent: (EssntlGalleryEvent) -> Unit,
    multipleSelect: Boolean,
    onAddImages: (images: List<FileModel>) -> Unit,
) {

    var selectedImageIds by remember {
        mutableStateOf(emptySet<String>())
    }

    val context = LocalPlatformContext.current

    val pickerLauncher = rememberFilePickerLauncher(
        type = FilePickerFileType.Image,
        selectionMode = FilePickerSelectionMode.Multiple,
        onResult = { result ->
            val imagePathList = result.map {
                FileModel.empty.copy(
                    id = Uuid.random().toString(),
                    url = it.getPath(context) ?: "",
                    name = it.getName(context) ?: "",
                    kmpFile = it,
                )
            }
            if (imagePathList.isNotEmpty())
                onEvent(EssntlGalleryEvent.AddImages(imagePathList))
        },
    )

    LaunchedEffect(Unit) {
        onEvent(
            EssntlGalleryEvent.SwitchTab(
                index = 0,
                dialog = true,
            ),
        )
    }

    Card(
        shape = RoundedCornerShape(25.dp),
        modifier = Modifier.fillMaxWidth(0.7f),
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center,
            modifier = Modifier
                .fillMaxWidth()
                .background(color = Color.White),
        ) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(color = MaterialTheme.esColorScheme.bgPrimary)
                    .padding(vertical = 3.spacing, horizontal = 2.5.spacing),
            ) {
                Text(
                    text = "Odia Gallery",
                    style = MaterialTheme.esTypography.h3Bold24,
                    color = MaterialTheme.esColorScheme.textPrimary,
                    modifier = Modifier.align(Alignment.CenterStart),
                )

                EsSmallOutlineIconButton(
                    onClick = onDismissRequest,
                    iconRes = EsIcons.Close,
                    modifier = Modifier.align(Alignment.CenterEnd),
                )
            }

            Spacer(Modifier.height(height = 3.5.spacing))

            GalleryGrid(
                state = state,
                onEvent = onEvent,
                selectedImageIds = selectedImageIds,
                deletable = false,
                onSelect = { isSelected, imageId ->
                    val oldSelectedPictureIds =
                        if (multipleSelect)
                            selectedImageIds
                        else
                            emptySet()

                    selectedImageIds =
                        if (isSelected)
                            oldSelectedPictureIds - imageId
                        else
                            oldSelectedPictureIds + imageId
                },
                onClick = { isSelected, imageId ->
                    val oldSelectedPictureIds =
                        if (multipleSelect)
                            selectedImageIds
                        else
                            emptySet()

                    selectedImageIds =
                        if (isSelected)
                            oldSelectedPictureIds - imageId
                        else
                            oldSelectedPictureIds + imageId
                },
                modifier = Modifier.heightIn(max = 440.dp),
            )

            Spacer(Modifier.height(height = 1.5.spacing))

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(
                        top = 1.5.spacing,
                        bottom = 2.5.spacing,
                        start = 2.5.spacing,
                        end = 2.5.spacing,
                    ),
            ) {
                EsLargePrimaryButton(
                    text = "Save photos",
                    onClick = {
                        onAddImages(
                            state.getAllImages.data.orEmpty()
                                .filter { it.id in selectedImageIds }
                                .map {
                                    if (it.url.startsWith("http"))
                                        it
                                    else
                                        it.copy(id = "")
                                },
                        )
                        onDismissRequest()
                    },
                    enabled = selectedImageIds.isNotEmpty(),
                    modifier = Modifier.weight(1f),
                )

                Spacer(modifier = Modifier.width(2.spacing))

                EsLargeOutlineButton(
                    onClick = {
                        pickerLauncher.launch()
                    },
                    text = "Upload",
                    modifier = Modifier.weight(1f),
                )
            }
        }
    }
}